import { format, isValid } from 'date-fns';
import {
  COLUMN_TO_FIELD_MAP,
  DATE_COLUMNS,
  DISPLAY_DATETIME_FORMAT,
  DISPLAY_DATE_FORMAT,
} from '../../TransportCloudTable.consts';
import { GroupedItems } from '../../TransportCloudTable.types';
import {
  mapStatusValue,
  mapTriangulationValue,
  mapWeighingValue,
} from '../../utils/tableUtils';
import { EditingPayload } from './cellEditing.types';

export const extractEditingPayload = (
  itemId: string,
  columnKey: string,
  groupedRows: GroupedItems[],
): EditingPayload | null => {
  const rowIdMatch = itemId.match(/^job-(\d+)$/);
  const planIdMatch = itemId.match(/^plan-(\d+)$/);
  if (!rowIdMatch && !planIdMatch) return null;

  const fieldName = COLUMN_TO_FIELD_MAP[columnKey] || columnKey;

  if (planIdMatch) {
    return {
      rowId: parseInt(planIdMatch[1], 10),
      fieldName,
    };
  }

  const rowId = parseInt(rowIdMatch![1], 10);

  let jobId = 0;
  for (const group of groupedRows) {
    for (const jobCodeGroup of group.jobCodeGroups) {
      const foundItem = jobCodeGroup.items.find(
        (itm) => getItemId(itm) === itemId,
      );
      if (foundItem) {
        jobId = foundItem.jobId;
        break;
      }
    }
    if (jobId) break;
  }

  return { rowId, jobId, fieldName };
};

export const getItemId = (item: any): string | null => {
  if (item.jobEntryId) return `job-${item.jobEntryId}`;
  if (item.shipmentPlanContainerId)
    return `plan-${item.shipmentPlanContainerId}`;
  if (item.lp) return `lp-${item.lp}`;
  return null;
};

const parseDateString = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ');
  const [day, month, year] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

export const parseDisplayDateTimeToEpoch = (
  displayValue: string,
): number | null => {
  try {
    const date = parseDateString(displayValue);
    return isValid(date) ? date.getTime() : null;
  } catch {
    return null;
  }
};

const toDate = (value: any): Date => {
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string' && value.includes('-') && value.includes(':')) {
    return parseDateString(value);
  }
  return new Date(value);
};

export const formatDateForDisplay = (value: any): string | null => {
  if (!value) return null;

  try {
    const date = toDate(value);
    return isValid(date) ? format(date, DISPLAY_DATETIME_FORMAT) : value;
  } catch {
    return value;
  }
};

export const areDatesOnDifferentDays = (date1: any, date2: any): boolean => {
  if (!date1 || !date2) return false;

  try {
    const d1 = toDate(date1);
    const d2 = toDate(date2);

    if (!isValid(d1) || !isValid(d2)) return false;

    return (
      d1.getFullYear() !== d2.getFullYear() ||
      d1.getMonth() !== d2.getMonth() ||
      d1.getDate() !== d2.getDate()
    );
  } catch {
    return false;
  }
};

export const formatDateOnly = (value: any): string => {
  const date = toDate(value);
  return isValid(date) ? format(date, DISPLAY_DATE_FORMAT) : String(value);
};

const formatTruckerRate = (value: any): string => {
  const rate = value.rate || value.amount || '';

  let currencyCode = '';
  if (typeof value.currency === 'string') {
    currencyCode = value.currency;
  } else if (typeof value.currency === 'object' && value.currency?.currency) {
    currencyCode = value.currency.currency;
  }

  return currencyCode ? `${rate} ${currencyCode}` : rate;
};

const formatCustomsClearance = (value: any, tradeType?: string): string => {
  const status = value.status || 'NONE';
  return tradeType ? mapStatusValue(status, tradeType) : status;
};

export const formatDisplayValue = (
  newValue: any,
  columnKey: string,
  tradeType?: string,
): any => {
  if (DATE_COLUMNS.includes(columnKey)) {
    return formatDateForDisplay(newValue);
  }

  if (columnKey === 'triangulation') {
    if (typeof newValue === 'string') {
      return mapTriangulationValue(newValue);
    }
    return newValue;
  }

  if (columnKey === 'weighing') {
    if (typeof newValue === 'string') {
      return mapWeighingValue(newValue);
    }
    return newValue;
  }

  if (typeof newValue === 'object' && newValue !== null) {
    if (columnKey === 'truckerRate') {
      return formatTruckerRate(newValue);
    }
    if (columnKey === 'customsClearance') {
      return formatCustomsClearance(newValue, tradeType);
    }
    if (columnKey === 'loadingDeliveryLocation') {
      if ('address' in newValue) {
        const city = newValue.address?.city || '';
        const lineTwo = newValue.address?.lineTwo || '';
        if (city && lineTwo) {
          return `${city}, ${lineTwo}`;
        } else if (city) {
          return `${city}, -`;
        } else if (lineTwo) {
          return `-, ${lineTwo}`;
        }

        return '-, -';
      }
      return newValue.code || '';
    }
    if ('code' in newValue) {
      return newValue.code;
    }
  }

  return newValue;
};

export const normalizeTruckerRate = (value: any): any => {
  if (typeof value === 'object' && value !== null) {
    const amount = value.rate || value.amount;

    const currencyObj =
      typeof value.currency === 'string'
        ? { currency: value.currency }
        : value.currency;

    return {
      rate: amount,
      currency: currencyObj,
    };
  }
  return value;
};

const getComparableValue = (value: any): any => {
  if (value == null) return null;
  if (typeof value === 'object') {
    return value.code ?? value.value ?? value.label ?? value.id ?? null;
  }
  return value;
};

const compareCustomsClearanceValues = (value1: any, value2: any): boolean => {
  const v1 =
    typeof value1 === 'object' ? value1 : { status: null, placeId: null };
  const v2 =
    typeof value2 === 'object' ? value2 : { status: null, placeId: null };
  return v1.status === v2.status && v1.placeId === v2.placeId;
};

const isTruckerRateEmpty = (value: any): boolean => {
  if (value == null) return true;

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (typeof value === 'object') {
    const rate = value.rate ?? value.amount ?? null;
    const currency = value.currency ?? null;

    const isRateEmpty = rate == null || rate === '';
    const isCurrencyEmpty = currency == null;

    return isRateEmpty && isCurrencyEmpty;
  }

  return true;
};

const compareTruckerRateValues = (value1: any, value2: any): boolean => {
  const v1 =
    typeof value1 === 'object' && value1 !== null
      ? value1
      : { rate: null, currency: null };
  const v2 =
    typeof value2 === 'object' && value2 !== null
      ? value2
      : { rate: null, currency: null };

  let rate1 = v1.rate ?? v1.amount ?? null;
  let rate2 = v2.rate ?? v2.amount ?? null;

  if (rate1 === '') rate1 = null;
  if (rate2 === '') rate2 = null;

  if (typeof rate1 === 'string' && rate1 !== '' && !isNaN(Number(rate1)))
    rate1 = Number(rate1);
  if (typeof rate2 === 'string' && rate2 !== '' && !isNaN(Number(rate2)))
    rate2 = Number(rate2);

  const currency1Id = v1.currency?.id ?? null;
  const currency2Id = v2.currency?.id ?? null;

  return rate1 === rate2 && currency1Id === currency2Id;
};

export const valuesAreEqual = (
  value1: any,
  value2: any,
  column: string,
): boolean => {
  if (column === 'truckerRate') {
    const isEmpty1 = isTruckerRateEmpty(value1);
    const isEmpty2 = isTruckerRateEmpty(value2);

    if (isEmpty1 && isEmpty2) {
      return true;
    }

    if (isEmpty1 !== isEmpty2) {
      return false;
    }

    const result = compareTruckerRateValues(value1, value2);
    return result;
  }

  if (value1 == null && value2 == null) return true;
  if (value1 == null || value2 == null) return false;

  if (column === 'customsClearance') {
    return compareCustomsClearanceValues(value1, value2);
  }

  const comparable1 = getComparableValue(value1);
  const comparable2 = getComparableValue(value2);

  if (comparable1 === comparable2) return true;

  if (
    typeof value1 === 'object' &&
    typeof value2 === 'object' &&
    'id' in value1 &&
    'id' in value2 &&
    value1.id != null &&
    value2.id != null
  ) {
    return value1.id === value2.id;
  }

  return value1 === value2;
};

export const findRowAndOldValue = (
  groupedRows: GroupedItems[],
  itemId: string,
  columnKey: string,
) => {
  for (const group of groupedRows) {
    for (const jobCodeGroup of group.jobCodeGroups) {
      const foundItem = jobCodeGroup.items.find(
        (itm) => getItemId(itm) === itemId,
      );
      if (foundItem) {
        return {
          rowData: foundItem,
          oldValue: foundItem[columnKey as keyof typeof foundItem],
        };
      }
    }
  }
  return { rowData: null, oldValue: null };
};

export const updateGroupedRows = (
  rows: GroupedItems[],
  itemId: string,
  columnKey: string,
  newValue: any,
): GroupedItems[] =>
  rows.map((group) => ({
    ...group,
    jobCodeGroups: group.jobCodeGroups.map((jobCodeGroup) => ({
      ...jobCodeGroup,
      items: jobCodeGroup.items.map((item) =>
        getItemId(item) === itemId ? { ...item, [columnKey]: newValue } : item,
      ),
    })),
  }));

const getFirstReferencePart = (value: any): string => {
  if (!value) return '';
  const str = String(value).trim();
  const match = str.match(/^([^,/-]+)/);
  return match ? match[1].trim() : '';
};

export const valuesMatchForCorrelation = (
  userValue: any,
  backendValue: any,
  columnKey: string,
): boolean => {
  if (columnKey === 'referenceNumbers') {
    const userFirstPart = getFirstReferencePart(userValue);
    const backendFirstPart = getFirstReferencePart(backendValue);

    if (!userFirstPart) return false;

    return backendFirstPart === userFirstPart;
  }

  return valuesAreEqual(userValue, backendValue, columnKey);
};

export interface EditableSelection {
  jobEntryIds: number[];
  shipmentPlanContainerIds: number[];
  editableItemIds: string[];
}

export const findEditableSelectedItems = (
  groupedRows: GroupedItems[],
  selectedIds: string[],
  isEditable: (item: any) => boolean = () => true,
): EditableSelection => {
  const jobEntryIds: number[] = [];
  const shipmentPlanContainerIds: number[] = [];
  const editableItemIds: string[] = [];

  selectedIds.forEach((selectedId) => {
    itemSearch: for (const group of groupedRows) {
      for (const jobCodeGroup of group.jobCodeGroups) {
        const foundItem = jobCodeGroup.items.find(
          (item) => getItemId(item) === selectedId,
        );
        if (
          foundItem &&
          (foundItem.jobEntryId || foundItem.shipmentPlanContainerId)
        ) {
          if (isEditable(foundItem)) {
            if (foundItem.jobEntryId) {
              jobEntryIds.push(foundItem.jobEntryId);
            } else if (foundItem.shipmentPlanContainerId) {
              shipmentPlanContainerIds.push(foundItem.shipmentPlanContainerId);
            }
            editableItemIds.push(selectedId);
          }
          break itemSearch;
        }
      }
    }
  });

  return { jobEntryIds, shipmentPlanContainerIds, editableItemIds };
};

export const buildMultiEditPayload = (
  payload: any,
  jobEntryIds: number[],
  shipmentPlanContainerIds: number[],
): any => {
  const applyToOne = (p: any) => ({
    ...p,
    rowIds: jobEntryIds.length ? jobEntryIds : undefined,
    shipmentPlanContainerIds: shipmentPlanContainerIds.length
      ? shipmentPlanContainerIds
      : undefined,
    rowId: undefined,
    shipmentPlanContainerId: undefined,
  });

  return Array.isArray(payload) ? payload.map(applyToOne) : applyToOne(payload);
};
